import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { pagesApi } from '@/lib/supabase';
import { useLocalizedContent } from '@/hooks/useLocalizedContent';
import ScrollHeading from '@/components/ScrollHeading';

const About = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { getField, getContent } = useLocalizedContent();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await pagesApi.getPublishedBySlug('about');
        setPageData(data);
      } catch (err) {
        console.error('Error fetching about page:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const sections = pageData?.sections || [];
  const bioSection = sections.find(s => s.section_type === 'text_with_image');
  const listSections = sections.filter(s => s.section_type === 'list');

  const bioContent = bioSection ? getContent(bioSection) : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7] px-6 md:px-12 lg:px-24 py-24 md:py-32">
      <div>
        <ScrollHeading 
          className="heading-display mb-16"
          dataTestId="about-heading"
        >
          {(pageData && getField(pageData, 'title')) || t('about.title')}
        </ScrollHeading>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 max-w-6xl">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-7 space-y-8"
          >
            {bioContent?.paragraphs?.map((paragraph, index) => (
              <p key={index} className="font-lexend font-light text-lg leading-relaxed tracking-wide text-[#1C1C1C]" data-testid={index === 0 ? "about-bio" : undefined}>
                {paragraph}
              </p>
            ))}

            {listSections.map((section) => {
              const sectionContent = getContent(section);
              return (
                <div key={section.id} className="pt-8 border-t border-[#E5E0D6]">
                  <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[#8C8C8C] mb-4">
                    {sectionContent?.label || section.title}
                  </h3>
                  {section.section_type === 'list' && sectionContent?.items && (
                    <ul className="font-lexend font-light text-lg leading-relaxed tracking-wide text-[#1C1C1C] space-y-2">
                      {sectionContent.items.map((item, index) => (
                        <li key={index}>— {item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </motion.div>

          {/* Image Column - shared across languages */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-5"
          >
            {bioSection?.content?.image_url && (
              <div className="aspect-[3/4] overflow-hidden border border-[#E5E0D6]">
                <img 
                  src={bioSection.content.image_url} 
                  alt={bioSection.content.image_alt || 'About image'}
                  className="w-full h-full object-cover grayscale"
                />
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default About;
